// src/pages/meeting/MeetingFutureList.js
import React, { useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import Spinner from "../../components/Spinner/Spinner";
import { getFutureMeetings } from "../../features/meeting/meetingSlice";
import { getEvents } from "../../features/event/eventSlice";
import {
  formatDateTime,
  meetingTypeLabel,
  toDate,
  isActivityMeeting,
  getDetailPath,
} from "../../utils/meetingUtils";

const isFuture = (d) => {
  const dt = toDate(d);
  return dt ? dt.getTime() >= Date.now() : false;
};
const isVisibleMeeting = (m) => m?.state !== false && m?.state !== 'false';
const isEvent = (m) => isActivityMeeting(m);
const getUserId = (user) => user?.id ?? user?._id ?? user?.uid ?? user?.userId ?? null;
const userInList = (users, uid) => {
  if (!uid || !Array.isArray(users)) return false;
  return users.some((u) => {
    if (typeof u === "string") return u === uid;
    if (typeof u === "object" && u) {
      return u.id === uid || u._id === uid || u.uid === uid || u.userId === uid;
    }
    return false;
  });
};

const SectionHeader = ({ title, linkTo, linkLabel }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ width: "4px", height: "22px", borderRadius: "4px", backgroundColor: "#5f60b9" }} />
      <h5 style={{ margin: 0, fontWeight: 700, fontSize: "1rem", color: "#1a1a2e" }}>{title}</h5>
    </div>
    <Link to={linkTo} style={{ fontSize: "0.8rem", color: "#5f60b9", textDecoration: "none", fontWeight: 500, whiteSpace: "nowrap" }}>
      {linkLabel} &raquo;
    </Link>
  </div>
);

const EmptyCard = ({ text }) => (
  <div style={{
    background: "#fff",
    borderRadius: "14px",
    padding: "24px 16px",
    textAlign: "center",
    color: "#aaa",
    fontSize: "0.875rem",
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)"
  }}>
    <i className="fa fa-inbox" style={{ fontSize: "24px", marginBottom: "8px", display: "block", color: "#d0d0d0" }} />
    {text}
  </div>
);

const CreateMeetingCard = () => (
  <Link
    to="/MeetingAdd"
    style={{
      background: "#fff",
      borderRadius: "14px",
      padding: "24px 16px",
      textAlign: "center",
      color: "#5f60b9",
      fontSize: "0.92rem",
      fontWeight: 700,
      boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      display: "block",
      textDecoration: "none",
      border: "1px dashed #c8c9f0",
    }}
  >
    <i className="fa fa-plus-circle" style={{ fontSize: "24px", marginBottom: "8px", display: "block" }} />
    Toplantı Oluştur
  </Link>
);

const InfoRow = ({ icon, text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
    <i className={`fa fa-${icon}`} style={{ color: "#5f60b9", fontSize: "13px", width: "14px", textAlign: "center" }} />
    <span style={{ color: "#555", fontSize: "0.82rem" }}>{text}</span>
  </div>
);

const formatHourMinute = (value) => {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

const getMeetingDurationText = (startDate, endDate) => {
  const start = toDate(startDate);
  const end = toDate(endDate);
  if (!start || !end) return "-";
  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "-";
  const totalMin = Math.round(diffMs / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h} sa ${m} dk`;
  if (h > 0) return `${h} sa`;
  return `${m} dk`;
};

function MeetingFutureList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const isAuthenticated = !!user;

  const {
    meetings = [],
    loadingStates: meetingLoadingStates,
    errors: meetingErrors,
    message: meetingsMessage,
  } = useSelector((state) => state.meeting);
  const isMeetingsLoading = meetingLoadingStates?.getFutureMeetings;
  const isMeetingsError = meetingErrors?.getFutureMeetings;

  const {
    events = [],
    loadingStates: eventLoadingStates,
    errors: eventErrors,
    message: eventsMessage,
  } = useSelector((state) => state.event);
  const isEventsLoading = eventLoadingStates?.getEvents;
  const isEventsError = eventErrors?.getEvents;

  useEffect(() => {
    if (!isAuthenticated) navigate("/login");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(getFutureMeetings());
    dispatch(getEvents());
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isMeetingsError) console.log(meetingsMessage);
  }, [isMeetingsError, meetingsMessage]);

  useEffect(() => {
    if (isEventsError) console.log(eventsMessage);
  }, [isEventsError, eventsMessage]);

  const formatDate = (date) => formatDateTime(date);

  const { upcomingEventsAll, upcomingMeetingsAll, myUpcomingEvents } = useMemo(() => {
    const upcomingMeetings = [...meetings].filter((m) => isVisibleMeeting(m) && isFuture(m?.startDate));
    upcomingMeetings.sort((a, b) => {
      const da = toDate(a?.startDate)?.getTime() ?? 0;
      const db = toDate(b?.startDate)?.getTime() ?? 0;
      return da - db;
    });

    const upcomingEvents = [...events].filter((e) => isFuture(e?.startDate));
    const onlyMeetings = upcomingMeetings.filter((m) => !isEvent(m));
    const uid = getUserId(user);
    const joinedFromMeetingsEvents = upcomingMeetings.filter(
      (e) => isEvent(e) && userInList(e?.users, uid)
    );

    return {
      upcomingEventsAll: upcomingEvents,
      upcomingMeetingsAll: onlyMeetings,
      myUpcomingEvents: joinedFromMeetingsEvents,
    };
  }, [meetings, events, user]);

  if (isMeetingsLoading || isEventsLoading) return <Spinner />;
  const hasMyEvents = myUpcomingEvents.length > 0;
  const hasLoadError = Boolean(isMeetingsError || isEventsError);

  const cardStyle = {
    background: "#fff",
    borderRadius: "14px",
    padding: "16px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
    borderLeft: "4px solid #5f60b9",
    textDecoration: "none",
    display: "block",
    transition: "box-shadow 0.2s",
  };

  const eventCardStyle = {
    background: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
    textDecoration: "none",
    display: "block",
  };

  const MeetingCard = ({ item }) => (
    <Link to={getDetailPath(item)} style={cardStyle}>
      <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", marginBottom: "8px" }}>
        {item.title}
      </div>
      {String(item?.status ?? '0') === '1' && (
        <div style={{ marginBottom: "8px" }}>
          <span className="badge light badge-danger">Toplantı İptal Edildi</span>
        </div>
      )}
      <InfoRow icon="calendar" text={formatDate(item.startDate)} />
      <InfoRow icon="clock-o" text={`Saat: ${formatHourMinute(item.startDate)} - ${formatHourMinute(item.endDate)}`} />
      <InfoRow icon="hourglass-half" text={`Süre: ${getMeetingDurationText(item.startDate, item.endDate)}`} />
      <InfoRow icon="briefcase" text="Toplantı" />
      <InfoRow icon="star" text={meetingTypeLabel(item.type)} />
      <InfoRow icon="user" text={`${item.users?.length ?? 0} Katılımcı`} />
    </Link>
  );

  const EventCardWithImage = ({ event }) => (
    <Link to={"/EventDetailVideo/" + event.meetingId} style={eventCardStyle}>
      <img
        src={event.coverPhoto}
        alt=""
        style={{ width: "100%", height: "140px", objectFit: "cover" }}
      />
      <div style={{ padding: "12px" }}>
        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", marginBottom: "6px" }}>
          {event.title}
        </div>
        <InfoRow icon="calendar" text={formatDate(event.startDate)} />
        <InfoRow icon="map-marker" text="Online" />
        <InfoRow icon="users" text={`${event.users?.length ?? 0} Katılımcı`} />
      </div>
    </Link>
  );

  return (
    <div style={{ padding: "4px 0" }}>
      {hasLoadError && (
        <div style={{
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          color: "#9a3412",
          borderRadius: "12px",
          padding: "10px 12px",
          marginBottom: "16px",
          fontSize: "0.86rem",
        }}>
          Bazı toplantılar yüklenemedi. Sayfayı yenileyebilir veya biraz sonra tekrar deneyebilirsiniz.
        </div>
      )}

      {/* Yaklaşan Etkinlikler */}
      {upcomingEventsAll.length > 0 && (
        <div style={{ marginBottom: "28px" }}>
          <SectionHeader title="Yaklaşan Etkinlikler" linkTo="/Discover" linkLabel="Tüm etkinlikleri gör" />
          <div className="row g-3">
            {upcomingEventsAll.map((e, idx) => (
              <div className="col-12 col-sm-6 col-xl-4" key={e.meetingId || e.id || idx}>
                <EventCardWithImage event={e} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toplantılarım + Etkinliklerim */}
      <div className="row g-3">
        <div className={`col-12 ${hasMyEvents ? "col-md-6" : ""}`}>
          <SectionHeader title="Toplantılarım" linkTo="/past" linkLabel="Tüm toplantılarım" />
          {upcomingMeetingsAll.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {upcomingMeetingsAll.map((m, idx) => (
                <MeetingCard key={m.meetingId || m.id || idx} item={m} />
              ))}
            </div>
          ) : <CreateMeetingCard />}
        </div>

        {myUpcomingEvents.length > 0 && (
          <div className="col-12 col-md-6">
            <SectionHeader title="Etkinliklerim" linkTo="/past" linkLabel="Tüm etkinliklerim" />
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {myUpcomingEvents.map((e, idx) => (
                <MeetingCard key={e.meetingId || e.id || idx} item={e} />
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default MeetingFutureList;
